'use client';

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { checkPasswordStrength } from '@/lib/auth-utils';

interface PasswordInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    showStrengthMeter?: boolean;
    showRequirements?: boolean;
    className?: string;
}

export default function PasswordInput({
    value,
    onChange,
    placeholder = 'Enter password',
    showStrengthMeter = false,
    showRequirements = false,
    className = '',
}: PasswordInputProps) {
    const [showPassword, setShowPassword] = useState(false);
    const strength = checkPasswordStrength(value);

    const getStrengthColor = (score: number) => {
        if (score === 0) return 'bg-muted';
        if (score <= 1) return 'bg-red-500';
        if (score === 2) return 'bg-orange-500';
        if (score === 3) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const getStrengthLabel = (score: number) => {
        if (score === 0) return '';
        if (score <= 1) return 'Weak';
        if (score === 2) return 'Fair';
        if (score === 3) return 'Good';
        return 'Strong';
    };

    const requirements = [
        { text: 'At least 8 characters', met: value.length >= 8 },
        { text: 'One uppercase letter', met: /[A-Z]/.test(value) },
        { text: 'One lowercase letter', met: /[a-z]/.test(value) },
        { text: 'One number', met: /\d/.test(value) },
    ];

    return (
        <div className={`space-y-2 ${className}`}>
            <div className="relative">
                <Input
                    type={showPassword ? 'text' : 'password'}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="pr-10"
                />
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                >
                    {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                </Button>
            </div>

            {showStrengthMeter && value.length > 0 && (
                <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Password Strength</span>
                        <span className={`font-medium ${strength.score <= 1 ? 'text-red-500' :
                                strength.score === 2 ? 'text-orange-500' :
                                    strength.score === 3 ? 'text-yellow-500' :
                                        'text-green-500'
                            }`}>
                            {getStrengthLabel(strength.score)}
                        </span>
                    </div>
                    <Progress
                        value={(strength.score / 4) * 100}
                        className={`h-2 ${getStrengthColor(strength.score)}`}
                    />
                </div>
            )}

            {showRequirements && value.length > 0 && (
                <div className="space-y-1 text-xs">
                    {requirements.map((req, index) => (
                        <div key={index} className="flex items-center gap-2">
                            {req.met ? (
                                <CheckCircle className="h-3 w-3 text-green-500" />
                            ) : (
                                <XCircle className="h-3 w-3 text-muted-foreground" />
                            )}
                            <span className={req.met ? 'text-green-500' : 'text-muted-foreground'}>
                                {req.text}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
